import Modal from '../Shared/Modal';
import { getBookingPageTheme, getThemedBg } from '../../utility/themeVars';

/**
 * Shared modal that renders a provider's photo, name, and full bio.
 * Used by both ProviderBioTrigger (Business Mode chip) and ProviderBioReadMore
 * (Solo Mode "Read more" link) so the modal body lives in one place.
 *
 * Purely presentational — the parent owns the open/onClose state.
 *
 * Returns null when none of photo, name, or bio are present.
 *
 * @param {boolean} open - Whether the modal is visible.
 * @param {Function} onClose - Callback invoked when the modal should close.
 * @param {Object} bookingPage - Booking page data object.
 */
const ProviderBioModal = ({ open, onClose, bookingPage }) => {
  const providerName = bookingPage?.providerName?.trim() || null;
  const providerBio = bookingPage?.providerBio || null;
  const profilePhotoUrl = bookingPage?.profilePhotoUrl || null;

  // Nothing to surface — don't mount the modal at all
  if (!providerName && !providerBio && !profilePhotoUrl) {
    return null;
  }

  const title = providerName ? `About ${providerName}` : 'About';
  const { themeMode, primaryColor } = getBookingPageTheme(bookingPage);
  const themedBg = getThemedBg(themeMode, primaryColor);
  const isDark = themedBg.isDark;

  // Body uses the themed CSS-var utility classes. Headless UI's Dialog portals
  // to document.body, escaping PublicBookingLayout's inline style={themeVars}
  // scope — so Modal re-injects the --theme-* vars onto the DialogPanel (via
  // themeStyle below), which makes var(--theme-*) resolve correctly here.

  return (
    <Modal open={open} onClose={onClose} title={title} size="md" themedBg={themedBg} themeStyle={themedBg.style}>
      <div className="flex flex-col items-center text-center">
        {profilePhotoUrl && (
          <img
            src={profilePhotoUrl}
            alt={providerName || 'Provider'}
            className={`h-40 w-40 rounded-full object-cover ring-1 ${themedBg ? 'ring-themed-border' : 'ring-ink-100'} ${isDark ? 'shadow-lift' : 'shadow-hairline'}`}
          />
        )}

        {providerName && (
          <h2 className={`mt-6 font-display text-2xl font-semibold break-words ${themedBg ? 'text-themed-primary' : 'text-ink-900'}`}>
            {providerName}
          </h2>
        )}

        {providerBio && (
          <>
            {(profilePhotoUrl || providerName) && (
              <div className={`mt-6 w-16 border-t ${themedBg ? 'border-themed' : 'border-ink-100'}`} aria-hidden="true" />
            )}
            <p className={`mt-6 max-w-md text-left text-[15px] leading-7 whitespace-pre-line ${themedBg ? 'text-themed-secondary' : 'text-ink-700'}`}>
              {providerBio}
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProviderBioModal;
